import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBulb, IconCircleCheck } from '@tabler/icons-react';
import TeeBadge from '@/components/ui/tee-badge';
import { IssueRichText } from '@/components/issue-notices/IssueRichText';
import { formatClock, formatDay, noticeZone } from '@/components/issue-notices/issue-notice-meta';
import type { IssueTimelineNotice } from '@/types/issue-notice';
import { cn } from '@/utils/cn';

interface Entry {
  key: string;
  kind: 'resolved' | 'update' | 'reported';
  at: string;
  by?: string;
  body: ReactNode;
}

/**
 * Status-page style history: the latest status first in a card, then earlier
 * updates, ending with the original report.
 */
export function IssueStatusTimeline({ notice }: { notice: IssueTimelineNotice }) {
  const { t } = useTranslation();
  const zone = noticeZone(notice);
  const resolved = notice.endReason === 'resolved';

  const workaround = (
    <div className="mt-3 rounded-[8px] border-l-4 border-[var(--color-gold-500)] bg-[var(--background-page-header)] p-4">
      <p className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-gold)]">
        <IconBulb size={16} aria-hidden />
        {t('issues.detail.workaround')}
      </p>
      {notice.workaround ? (
        <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-[var(--text-content-title)]">{notice.workaround}</p>
      ) : (
        <p className="mt-2 text-[14px] text-[var(--text-content-sub-title)]">{t('issues.detail.noWorkaround')}</p>
      )}
    </div>
  );

  const entries: Entry[] = [];
  if (resolved) {
    entries.push({
      key: 'resolved',
      kind: 'resolved',
      at: notice.endedAt ?? notice.updatedAt ?? notice.startsAt,
      body: <p className="whitespace-pre-line">{notice.resolutionNote || t('issues.detail.resolvedDefault')}</p>,
    });
  }
  [...notice.updates]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .forEach((u) => entries.push({ key: u.id, kind: 'update', at: u.createdAt, by: u.createdByName, body: <IssueRichText html={u.body} /> }));
  entries.push({
    key: 'reported',
    kind: 'reported',
    at: notice.publishedAt ?? notice.startsAt,
    by: notice.createdByName,
    body: <IssueRichText html={notice.description} />,
  });

  const [latest, ...earlier] = entries;
  // While the issue is open the workaround stays next to the latest status, not buried at the bottom.
  const pinWorkaround = !resolved;

  return (
    <div className="flex flex-col gap-6">
      <div
        className={cn(
          'rounded-[8px] border p-5',
          latest.kind === 'resolved' ? 'border-[var(--border-active)] bg-[var(--background-badge-green)]' : 'border-[var(--border-default)] bg-[var(--background-card)]',
        )}
      >
        <EntryHeader entry={latest} latest />
        <div className="mt-2 text-[17px] leading-relaxed text-[var(--text-content-title)]">{latest.body}</div>
        <EntryMeta entry={latest} zone={zone} />
        {latest.kind === 'reported' && pinWorkaround && workaround}
      </div>

      {pinWorkaround && latest.kind !== 'reported' && workaround}

      {earlier.length > 0 && (
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-gold)]">{t('issues.detail.earlierUpdates')}</p>
          <div className="mt-3 flex flex-col">
            {earlier.map((e) => (
              <div key={e.key} className="relative border-l-2 border-[var(--border-default)] pb-6 pl-5 last:pb-0">
                <span aria-hidden className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 border-[var(--background-card)] bg-[var(--border-default)]" />
                <EntryHeader entry={e} />
                <div className="mt-1 text-[15px] leading-relaxed text-[var(--text-content-title)]">{e.body}</div>
                <EntryMeta entry={e} zone={zone} />
                {e.kind === 'reported' && !pinWorkaround && notice.workaround && workaround}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EntryHeader({ entry, latest }: { entry: Entry; latest?: boolean }) {
  const { t } = useTranslation();
  const isResolved = entry.kind === 'resolved';
  return (
    <div className="flex flex-wrap items-center gap-2">
      {isResolved && <IconCircleCheck size={20} aria-hidden className="text-[var(--color-forest)]" />}
      <span className={cn('font-semibold', latest ? 'text-[16px]' : 'text-[14px]', isResolved ? 'text-[var(--color-forest)]' : 'text-[var(--text-content-title)]')}>
        {t(`issues.detail.${entry.kind}`)}
      </span>
      {latest && <TeeBadge size="s" type="green" label={t('issues.detail.latest')} />}
    </div>
  );
}

function EntryMeta({ entry, zone }: { entry: Entry; zone?: string }) {
  return (
    <p className="mt-2 text-[12px] text-[var(--text-content-sub-title)]">
      {formatDay(entry.at, zone)}, {formatClock(entry.at, zone)}
      {entry.by ? ` · ${entry.by}` : ''}
    </p>
  );
}
